/**
 * Accès catalogue côté UI.
 * Les données viennent de Shopify ; le catalogue local sert de repli
 * tant que la requête Shopify n'a pas abouti (aucune régression visuelle).
 */
import { useQuery } from "@tanstack/react-query";
import { newArrivals, productDetails, type Product } from "@/lib/catalog";
import { shopifyProductsQuery, type ShopProduct } from "@/lib/shopify-catalog";

const localDetails = Object.values(productDetails);

export function useShopifyProducts() {
  const { data, isLoading, isError } = useQuery(shopifyProductsQuery);
  return { products: data ?? [], isLoading, isError, ready: !!data && data.length > 0 };
}

/** Produits d'une collection (route) avec repli local. */
export function useCollectionProducts(
  route: string,
  localFallback: Product[],
  categoryRe?: RegExp,
): Product[] {
  const { products, ready } = useShopifyProducts();
  if (!ready) return localFallback;
  const filtered = products.filter(
    (p) => p.categoryTo === route || (categoryRe ? categoryRe.test(`${p.category} ${p.subcategory}`) : false),
  );
  return filtered.length > 0 ? filtered : localFallback;
}

/** Nouveautés / grille générale. */
export function useAllProducts(localFallback: Product[] = newArrivals): Product[] {
  const { products, ready } = useShopifyProducts();
  return ready ? products : localFallback;
}

/** Fiche produit : Shopify prioritaire, repli sur la fiche locale. */
export function useProductDetail(slug: string): {
  product: ShopProduct | (typeof localDetails)[number] | undefined;
  isLoading: boolean;
} {
  const { products, isLoading } = useShopifyProducts();
  const shopify = products.find((p) => p.slug === slug || p.handle === slug);
  return { product: shopify ?? productDetails[slug], isLoading };
}
